import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { List, Divider, ListItemIcon, ListSubheader, ListItemButton } from '@mui/material';
import { useSetIncarnateModal } from '../../features/global/hooks';
import { selectPaymentType, setPaymentType } from '../../features/webshop/globalSlice';
import { useIsLoggedIn, useUser, useUserRole as useUserRoleHook, useRoles, useIsIncarnated } from '../../features/auth/hooks/useAuthState';
import { useGetUnreadCountQuery } from '../../features/notifications/notificationsApi';
import { useFeatures } from '../../features/settings/hooks/useSettingsState';
import { Flex, Icon, theme, Typo } from '../../ui';
import { useModule } from '../../hooks/readFromUrls';
import { MODULES_LIST } from '../../utils/constants';
import { useWakeUp } from '../../features/auth/hooks/useAuthActions';

const MenuHeader = styled.div`
  display: flex;
  padding: 1rem;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 99;
  background-color: ${(props) => props.theme.colors.NEU_0};
  border-width: 0;
  border-style: solid;
  border-color: ${(props) => props.theme.colors.NEU_100};
  border-bottom-width: thin;
  gap: 10px;
`;
const UserDescription = styled.div`
  width: 210px;
  overflow-wrap: break-word;
  margin-left: 56px;
`;
const ItemDescription = styled(Typo.MediumText)`
  font-weight: 400;
`;
const ListItemButtonStyled = styled(ListItemButton)`
  &.Mui-selected {
    background-color: ${theme.colors.PRIMARY_EXTRA_LIGHT} !important;
  }
`;
const FlexStyled = styled(Flex)`
  margin: 1rem 0 0.5rem;
`;

const AppMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const wakeUp = useWakeUp();
  const setIncarnateModal = useSetIncarnateModal();

  const user = useUser();
  const myStore = user?.username;
  const loggedIn = useIsLoggedIn();
  const userRole = useUserRoleHook();
  const paymentType = useSelector(selectPaymentType);
  const { data: unreadNotifications } = useGetUnreadCountQuery(undefined, {
    skip: !loggedIn,
  });

  const roles = useRoles();
  const module = useModule();
  const isAdmin = roles && (roles[0] === 'dark-admin' || roles[0] === 'assistant');
  const isIncarnated = useIsIncarnated();
  const features = useFeatures();

  useEffect(() => {
    setSelectedIndex(MODULES_LIST[module]?.index);
  }, [module]);

  const handleTestPayment = useCallback(() => {
    dispatch(setPaymentType(paymentType === 9 ? 6 : 9));
  }, [dispatch, paymentType]);

  const handleLogOut = useCallback(() => {
    navigate('/logout');
  }, [navigate]);

  const openIncarnationModal = useCallback(() => {
    setIncarnateModal(true);
  }, [setIncarnateModal]);

  const wakeUpFromIncarnation = useCallback(() => {
    wakeUp();
  }, [wakeUp]);

  const handleSupportLink = useCallback(() => {
    let supportLink = document.createElement('a');
    supportLink.href = 'https://support.coursio.com/hc/sv';
    supportLink.target = '_blank';
    supportLink.rel = 'noopener noreferrer';
    supportLink.click();
  }, []);

  const handleListItemClick = useCallback(
    (index, to) => {
      navigate(to);
    },
    [navigate],
  );

  const Profile = useMemo(() => {
    return (
      <MenuHeader>
        <UserDescription>
          <ItemDescription>{user?.displayName}</ItemDescription>
          <Typo.Info>{t(`roles.${userRole}`)}</Typo.Info>
        </UserDescription>
      </MenuHeader>
    );
  }, [userRole, t, user]);

  const ListItem = useCallback(
    (item) => {
      const ModuleIcon = Icon[item.icon];
      return (
        <ListItemButtonStyled
          key={item.index}
          selected={selectedIndex === item.index}
          onClick={() => {
            const url = typeof item.url === 'string' ? item.url : item.url(myStore);
            return handleListItemClick(item.index, url);
          }}
        >
          <ListItemIcon>
            <ModuleIcon color={theme.colors.PRIMARY} />
          </ListItemIcon>
          <ItemDescription>{item.label(t, unreadNotifications)}</ItemDescription>
        </ListItemButtonStyled>
      );
    },
    [selectedIndex, t, handleListItemClick, unreadNotifications, myStore],
  );

  const ifSameRoles = useCallback((givenRoles) => roles?.filter((el) => givenRoles.indexOf(el) >= 0).length > 0, [roles]);

  const isItemVisible = useCallback(
    (item) => {
      // Check role access
      if (!ifSameRoles(item.accessRights)) return false;
      // Check feature flag if specified
      if (item.featureFlag && !features?.[item.featureFlag]) return false;
      return true;
    },
    [ifSameRoles, features],
  );

  const renderPanelItems = useCallback(() => {
    let panelItems = [];
    for (let key in MODULES_LIST) {
      if (isItemVisible(MODULES_LIST[key])) {
        panelItems = [
          ...panelItems,
          {
            section: MODULES_LIST[key].section,
            withoutSectionName: MODULES_LIST[key].withoutSectionName,
          },
        ];
      }
    }
    return [...new Map(panelItems.map((v) => [v.section, v])).values()];
  }, [isItemVisible]);

  const subModuleRender = useCallback(
    (sectionName) => {
      return Object.keys(MODULES_LIST).map((key) => {
        const item = MODULES_LIST[key];
        if (isItemVisible(item)) return item.section === sectionName && ListItem(item);

        return null;
      });
    },
    [ListItem, isItemVisible],
  );

  const subSectionRender = (action, icon, description) => {
    const PassedIcon = Icon[icon];
    return (
      <ListItemButtonStyled onClick={action}>
        <ListItemIcon>
          <PassedIcon color={theme.colors.PRIMARY} />
        </ListItemIcon>
        <ItemDescription>{description}</ItemDescription>
      </ListItemButtonStyled>
    );
  };

  const sectionRender = (sectionName, withoutSectionName) => {
    return withoutSectionName ? (
      <List key={sectionName}>{subModuleRender(sectionName)}</List>
    ) : (
      <Fragment key={sectionName}>
        <Divider />
        <List
          subheader={
            <ListSubheader>
              <FlexStyled gap='5' align='center'>
                <Typo.ContentSmallHeader color={theme.colors.COURSIO}>:</Typo.ContentSmallHeader>
                <Typo.ContentSmallHeader>{t(`menu.${sectionName}`)}</Typo.ContentSmallHeader>
              </FlexStyled>
            </ListSubheader>
          }
        >
          {subModuleRender(sectionName)}
        </List>
      </Fragment>
    );
  };

  return (
    <>
      {Profile}
      {renderPanelItems().map((item) => sectionRender(item.section, item.withoutSectionName))}
      <Divider />
      <List>
        {(isAdmin || isIncarnated) && (
          <>
            {subSectionRender(handleTestPayment, 'ToggleOn', `Payments with: ${paymentType === 9 ? 'Klarna' : 'Stripe'}`)}
            {!isIncarnated && subSectionRender(openIncarnationModal, 'Fingerprint', t('globals.incarnate'))}
            {isIncarnated && subSectionRender(wakeUpFromIncarnation, 'Yoga', t('globals.wakeUp'))}
          </>
        )}
      </List>
      <List>
        {userRole === 'reader' && subSectionRender(handleSupportLink, 'Support', t('globals.support'))}
        {subSectionRender(handleLogOut, 'Logout', t('auth.logout'))}
      </List>
    </>
  );
};

export default AppMenu;
